import type { GameClient } from "../net";
import { compressImage, uploadPhoto } from "../api";
import { enabledPhotoTypes, PHOTO_BY_ID } from "../photoTypes";

// Hider "Send a photo" flow: opens the phone's camera/library, then a review sheet
// to caption + pick ONE seeking team (all seekers in group mode) and send. The
// image is downscaled + EXIF-stripped client-side, uploaded to R2, then announced
// to the chosen squad as a 'photo' clue.
const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]!));

// opts.target pre-selects a team (e.g. replying to that team's question); opts.replyTo
// attaches the photo to a specific question so it shows inline in the Q&A.
export interface PhotoSendOpts { target?: string; replyingTo?: string; replyTo?: string }

// Seeker side: upload a photo into the squad's Photos clues, optionally tagged with
// one of the photo categories + a caption. Saved squad-only (never to the hider).
export function openPhotoClue(client: GameClient) {
  const input = document.createElement("input");
  input.type = "file"; input.accept = "image/*"; input.setAttribute("capture", "environment");
  input.style.display = "none";
  document.body.appendChild(input);
  input.addEventListener("change", () => { const file = input.files?.[0]; input.remove(); if (file) showClueReview(client, file); });
  input.click();
}
function showClueReview(client: GameClient, file: File) {
  const types = enabledPhotoTypes(client.config.photoTypes);
  let cat = "";
  let busy = false;
  const url = URL.createObjectURL(file);
  const overlay = document.createElement("div");
  overlay.className = "photo-modal";
  overlay.innerHTML = `
    <div class="photo-sheet">
      <div class="photo-sheet-hd"><span>Save a photo clue</span><button class="photo-x" aria-label="Cancel">✕</button></div>
      <img class="photo-preview" src="${url}" alt="preview"/>
      <input class="photo-caption" maxlength="140" placeholder="Add a caption (optional)…"/>
      <div class="photo-tgt-lbl">CATEGORY (optional)</div>
      <div class="ask-photogrid photo-catgrid">${types.map((p) => `<button class="ask-cat" data-cat="${p.id}">${esc(p.name)}</button>`).join("")}</div>
      <button class="primary photo-send">💾 Save to Photos</button>
      <div class="photo-status"></div>
    </div>`;
  document.body.appendChild(overlay);
  const sheet = overlay.querySelector<HTMLElement>(".photo-sheet")!;
  const statusEl = overlay.querySelector<HTMLElement>(".photo-status")!;
  const captionEl = overlay.querySelector<HTMLInputElement>(".photo-caption")!;
  const close = () => { if (busy) return; URL.revokeObjectURL(url); overlay.remove(); };
  overlay.addEventListener("click", (e) => { if (e.target === overlay) close(); });
  overlay.querySelector(".photo-x")?.addEventListener("click", close);
  sheet.querySelectorAll<HTMLButtonElement>("[data-cat]").forEach((b) => b.addEventListener("click", () => { cat = cat === b.dataset.cat ? "" : b.dataset.cat!; sheet.querySelectorAll("[data-cat]").forEach((x) => x.classList.toggle("on", x === b && !!cat)); }));
  overlay.querySelector<HTMLButtonElement>(".photo-send")!.addEventListener("click", async () => {
    if (busy) return;
    busy = true; statusEl.textContent = "Compressing…";
    try {
      const blob = await compressImage(file);
      statusEl.textContent = "Saving…";
      const key = await uploadPhoto(client.gameId, client.seekerKey, blob);
      const label = [cat ? PHOTO_BY_ID[cat]?.name : "", captionEl.value.trim()].filter(Boolean).join(" — ");
      client.addClue("photo", label, "manual", key);
      statusEl.textContent = "✓ Saved!";
      busy = false; setTimeout(close, 800);
    } catch { busy = false; statusEl.textContent = "Couldn't save — check your connection and retry."; }
  });
}

export function openPhotoSend(client: GameClient, opts: PhotoSendOpts = {}) {
  const input = document.createElement("input");
  // No `capture` attribute: mobile then offers the full picker (Take Photo OR
  // Photo Library), so the hider can upload an existing shot, not just snap a new one.
  input.type = "file"; input.accept = "image/*";
  input.style.display = "none";
  document.body.appendChild(input);
  input.addEventListener("change", () => { const file = input.files?.[0]; input.remove(); if (file) showReview(client, file, opts); });
  input.click();
}

function showReview(client: GameClient, file: File, opts: PhotoSendOpts) {
  const targets = client.photoTargets || [];
  const preset = !!opts.target;
  let targetKey = opts.target || (targets.length === 1 ? targets[0].key : "");
  let busy = false;
  const url = URL.createObjectURL(file);

  const overlay = document.createElement("div");
  overlay.className = "photo-modal";
  overlay.innerHTML = `
    <div class="photo-sheet">
      <div class="photo-sheet-hd"><span>Send a photo</span><button class="photo-x" aria-label="Cancel">✕</button></div>
      <img class="photo-preview" src="${url}" alt="preview"/>
      <input class="photo-caption" maxlength="140" placeholder="Add a caption (optional)…"/>
      ${preset
        ? `<div class="photo-tgt-lbl">${opts.replyTo ? "REPLYING TO" : "SENDING TO"}</div><div class="photo-replyline">${esc(opts.replyingTo || "the asking team")}</div>`
        : `<div class="photo-tgt-lbl">SEND TO ${client.mode === "group" ? "" : "(one team)"}</div>
           <div class="photo-targets">${
             targets.length
               ? targets.map((t) => `<button class="photo-tgt ${t.key === targetKey ? "on" : ""}" data-tgt="${esc(t.key)}">${esc(t.label)}${t.members.length ? ` · ${esc(t.members.join(", "))}` : ""}</button>`).join("")
               : `<div class="photo-empty">No seeking teams are online to send to yet.</div>`
           }</div>`}
      <button class="primary photo-send" ${targetKey ? "" : "disabled"}>📤 Send photo</button>
      <div class="photo-status"></div>
    </div>`;
  document.body.appendChild(overlay);

  const sheet = overlay.querySelector<HTMLElement>(".photo-sheet")!;
  const sendBtn = overlay.querySelector<HTMLButtonElement>(".photo-send")!;
  const statusEl = overlay.querySelector<HTMLElement>(".photo-status")!;
  const captionEl = overlay.querySelector<HTMLInputElement>(".photo-caption")!;
  const close = () => { if (busy) return; URL.revokeObjectURL(url); overlay.remove(); };

  overlay.addEventListener("click", (e) => { if (e.target === overlay) close(); });
  overlay.querySelector(".photo-x")?.addEventListener("click", close);
  sheet.querySelectorAll<HTMLButtonElement>("[data-tgt]").forEach((b) => b.addEventListener("click", () => {
    targetKey = b.dataset.tgt!;
    sheet.querySelectorAll("[data-tgt]").forEach((x) => x.classList.toggle("on", x === b));
    sendBtn.disabled = false;
  }));
  sendBtn.addEventListener("click", async () => {
    if (busy || !targetKey) return;
    busy = true; sendBtn.disabled = true; statusEl.textContent = "Compressing…";
    try {
      const blob = await compressImage(file);
      statusEl.textContent = "Sending…";
      const key = await uploadPhoto(client.gameId, targetKey, blob);
      // A photo is a thread message → shows inline in the Q&A (attached to a
      // question when replyTo is set) and in the seeker's Photos section.
      client.postMessage(targetKey, captionEl.value.trim(), "Photo", { photoKey: key, replyTo: opts.replyTo });
      statusEl.textContent = "✓ Sent!";
      busy = false; setTimeout(close, 900);
    } catch {
      busy = false; sendBtn.disabled = false; statusEl.textContent = "Couldn't send — check your connection and retry.";
    }
  });
}
